import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * FIELD
 *
 * The wrapper that pairs a label, a control, optional helper text and an error
 * message. Getting the vertical rhythm right here is what makes a form look
 * considered, so it is a recipe of its own rather than ad-hoc spacing at each
 * call site.
 */
export const fieldRecipe = cva([uiMarker, 'flex w-full min-w-0 flex-col'], {
  variants: {
    /** Vertical gap between label, control and messages. */
    density: {
      compact: 'gap-1',
      comfortable: 'gap-1.5',
      relaxed: 'gap-2',
    },
  },
  defaultVariants: { density: 'comfortable' },
});

export type FieldRecipeProps = VariantProps<typeof fieldRecipe>;

/** The field's label. */
export const fieldLabelRecipe = cva(
  [
    uiMarker,
    'font-sans text-label text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
  ],
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed opacity-70',
        false: '',
      },
      /**
       * `sm` is the dense label, for a form drawn at the detail-card size.
       *
       * Same 14px medium; muted rather than foreground, because at that
       * density the labels outnumber the values and a column of black labels
       * reads as the content. ReadOnlyField and Field both take it, so a
       * read-only box and an editable one in the same grid are labelled the
       * same way.
       */
      size: {
        md: '',
        sm: 'text-muted-foreground',
      },
    },
    defaultVariants: { disabled: false, size: 'md' },
  }
);

export type FieldLabelRecipeProps = VariantProps<typeof fieldLabelRecipe>;

/** Helper text under the control — format hints, units, context. */
export const fieldDescriptionRecipe = cva([
  uiMarker,
  'font-sans text-caption text-muted-foreground',
]);

/** Validation message. Replaces the description when present. */
export const fieldMessageRecipe = cva([uiMarker, 'font-sans text-caption'], {
  variants: {
    tone: {
      error: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
    },
  },
  defaultVariants: { tone: 'error' },
});

/** Marker appended to the label of a required field. */
export const fieldRequiredMarkerRecipe = cva([
  uiMarker,
  'ml-0.5 text-destructive',
]);

/**
 * FORM LAYOUT
 *
 * A responsive grid for laying fields out. Single column on mobile in every
 * case — dispatchers use this on phones.
 */
export const formLayoutRecipe = cva([uiMarker, 'grid w-full'], {
  variants: {
    columns: {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    },
    gap: {
      compact: 'gap-3',
      comfortable: 'gap-4',
      relaxed: 'gap-6',
    },
  },
  defaultVariants: { columns: 2, gap: 'comfortable' },
});

/** The row of buttons that closes a form. */
export const formActionsRecipe = cva(
  [uiMarker, 'flex flex-col-reverse gap-2 sm:flex-row sm:items-center'],
  {
    variants: {
      align: {
        start: 'sm:justify-start',
        end: 'sm:justify-end',
        between: 'sm:justify-between',
      },
      /** Adds a rule above the actions to separate them from the fields. */
      divided: {
        true: 'border-t border-border pt-4',
        false: '',
      },
    },
    defaultVariants: { align: 'end', divided: false },
  }
);

/**
 * The box a read-only value sits in.
 *
 * Shaped like a control on purpose: it appears in forms beside editable
 * Fields, and a bare line of text next to an Input makes a two-column grid
 * look broken. It is deliberately *not* `controlChrome` — no border, no
 * shadow — because it must not look typeable. Filled and flat reads as "this
 * came with the record" rather than "you left this blank".
 *
 * Matches FieldValueButton exactly — same height, padding and fill — because
 * the two sit in the same panels and only one of them is clickable. A reader
 * should be able to tell which by trying it, not by the box being a different
 * shape.
 */
export const readOnlyValueRecipe = cva(
  [uiMarker, 'flex w-full min-w-0 items-center text-foreground'],
  {
    variants: {
      /**
       * `sm` is the detail-panel shape: 32 high, tighter type, one line.
       *
       * It is the one place the "filled and flat" rule above is dropped. A
       * read-only panel is a page of nothing but these, with no Inputs beside
       * them to be confused with, so the fill that reads as "not typeable" in
       * a form reads as a grey slab there. Bordered and on the page background
       * is what the design asks for, and every box in that panel is one of
       * these, so nothing is left to mistake it for.
       */
      size: {
        md: 'min-h-control-md rounded-md bg-muted/50 px-3 py-2 text-body-sm',
        sm: 'min-h-control-sm gap-1 rounded-lg border border-border bg-background px-2 text-label',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

export type ReadOnlyValueRecipeProps = VariantProps<typeof readOnlyValueRecipe>;
